import type { ReactNode, Ref } from 'react';
import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import HelpOutlineOutlined from '@mui/icons-material/HelpOutlineOutlined';
import { SubmitButton } from './SubmitButton';
import { resolveTone } from './statusSeverity';

export type ConfirmTone = 'neutral' | 'warning' | 'danger';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** Dialog title (translated copy). */
  title: string;
  /** Body message (translated copy). */
  message?: ReactNode;
  /** Confirm button label (translated copy). */
  confirmLabel: string;
  /** Cancel button label (translated copy). */
  cancelLabel: string;
  /** Tone; `danger` renders the confirm button red, `warning` amber. */
  tone?: ConfirmTone;
  /** Shows a spinner on the confirm button and blocks input. */
  pending?: boolean;
  /** Externally disable confirm (e.g. failing precondition). */
  confirmDisabled?: boolean;
  /**
   * Type-name-to-confirm: when set, confirm stays disabled until the user types
   * this exact phrase. Use for destructive, hard-to-undo actions.
   */
  confirmPhrase?: string;
  /** Instruction shown above the phrase input (translated copy). */
  confirmPhraseHint?: ReactNode;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Confirmation modal on MUI `Dialog`. Supports a tone (neutral/warning/danger)
 * and an optional type-name-to-confirm guard for destructive operations.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  tone = 'neutral',
  pending = false,
  confirmDisabled = false,
  confirmPhrase,
  confirmPhraseHint,
  ref,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  // Reset the typed phrase on every open/close transition, computed during
  // render (React's sanctioned "reset state when a prop changes" pattern) so we
  // avoid a setState-in-effect cascade.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    setTyped('');
  }

  const theme = useTheme();
  const phraseBlocks = confirmPhrase ? typed.trim() !== confirmPhrase : false;
  const confirmColor = tone === 'danger' ? 'error' : tone === 'warning' ? 'warning' : 'primary';
  const toneColors = resolveTone(
    theme,
    tone === 'danger' ? 'error' : tone === 'warning' ? 'warning' : 'neutral',
  );
  const ToneIcon =
    tone === 'warning'
      ? WarningAmberOutlined
      : tone === 'danger'
        ? ErrorOutlineOutlined
        : HelpOutlineOutlined;

  return (
    <Dialog
      ref={ref}
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: '8px' } } }}
    >
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(toneColors.main, 0.12),
          }}
        >
          <ToneIcon sx={{ fontSize: 20, color: toneColors.main }} />
        </Box>
        <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700 }}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {message && <DialogContentText sx={{ fontSize: 14 }}>{message}</DialogContentText>}
        {confirmPhrase && (
          <Box sx={{ mt: 2 }}>
            {confirmPhraseHint && (
              <DialogContentText sx={{ mb: 1, fontSize: 13 }}>
                {confirmPhraseHint}
              </DialogContentText>
            )}
            <TextField
              fullWidth
              size="small"
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={confirmPhrase}
              error={phraseBlocks && typed.length > 0}
              slotProps={{
                htmlInput: { style: { fontFamily: 'var(--font-mono, ui-monospace, monospace)' } },
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          {cancelLabel}
        </Button>
        <SubmitButton
          type="button"
          color={confirmColor}
          pending={pending}
          disabled={phraseBlocks || confirmDisabled}
          onClick={onConfirm}
        >
          {confirmLabel}
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
}
